import getBriefingBySlug from '~/queries/get-briefing-by-slug'
import getBriefingsQuery from '~/queries/get-briefings'
import { logError } from '~/utils/log-error'
import { getAdaptedBriefings, getAdaptedBriefing } from '~/utils/adapters'
import type {
  TBriefing,
  TCategoryBriefings,
  TResponse,
} from '~/types/briefings'
import type { TGeneral } from '~/types/app.type'
import { useAuthStore } from '~/stores/auth'

export const useBriefings = () => {
  const { $apollo } = useNuxtApp()
  const authStore = useAuthStore()
  const isAuthenticated = computed(() => authStore.isAuthenticated)

  const getBriefings = async (
    categories: TCategoryBriefings[],
    limit: number = -1,
  ): Promise<TBriefing[]> => {
    try {
      const gqlClient = $apollo.getClient()
      const response = await gqlClient.query({
        query: getBriefingsQuery(categories),
      })

      if (response?.errors || !response?.data) return []

      const briefings = getAdaptedBriefings(response, isAuthenticated.value)

      if (limit > 0) {
        return briefings.slice(0, limit)
      }

      return briefings
    } catch (error) {
      logError(error)
      return []
    }
  }

  const getBriefing = async (params: {
    category: string
    slug: string
  }): Promise<TResponse<TBriefing>> => {
    try {
      const postTypePlural = kebabToCamelCase(params.category)

      const gqlClient = $apollo.getClient()
      const response = await gqlClient.query({
        query: getBriefingBySlug({
          slug: params.slug,
          postTypePlural,
        }),
      })

      if (response?.errors || !response?.data) {
        return {
          error: {
            message: 'Briefing not found',
            code: 'NOT_FOUND',
            httpStatus: 404,
          },
          data: null,
        }
      }

      if (response?.errors || !response?.data) {
        return {
          error: {
            message: 'Briefing not found',
            code: 'NOT_FOUND',
            httpStatus: 404,
          },
          data: null,
        }
      }

      const [post]: TGeneral[] = Object.values(response.data)
      const briefing = getAdaptedBriefing(
        post,
        postTypePlural,
        isAuthenticated.value,
      )

      return {
        data: briefing,
        error: null,
      }
    } catch (error) {
      logError(error)
      return {
        error: null,
        data: null,
      }
    }
  }

  return { getBriefings, getBriefing }
}
